'use client'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { fitsPlanLimit } from '@/lib/billing/utils/plan-limits'
import { cn } from '@/lib/utils'

interface ShopifyStoreUsageIndicatorProps {
  count: number
  /** null = unlimited */
  limit: number | null
  domains: string[]
  loading?: boolean
  className?: string
}

/**
 * Compact `N / M stores` badge with a tooltip listing every Shopify domain
 * the tenant currently uses (active integrations + custom pins on agents,
 * deduped). Rendered inside `ShopDomainField` next to the label and meant
 * to be reusable in plan cards / billing summaries later.
 */
export function ShopifyStoreUsageIndicator({
  count,
  limit,
  domains,
  loading,
  className,
}: ShopifyStoreUsageIndicatorProps) {
  if (loading) {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
          'bg-gray-100 text-gray-500 border-gray-300',
          className,
        )}
      >
        … / … stores
      </span>
    )
  }

  const overLimit = !fitsPlanLimit(count, limit)
  const atLimit = limit !== null && !overLimit && count === limit && limit > 0
  const limitText = limit === null ? 'unlimited' : limit

  return (
    // Self-contained provider so this shared badge is safe to render in any tree
    // (voice / email / messaging) without each parent supplying a TooltipProvider.
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide cursor-help',
              overLimit && 'bg-red-100 text-red-800 border-red-300',
              atLimit && 'bg-amber-100 text-amber-800 border-amber-300',
              !overLimit && !atLimit && 'bg-emerald-50 text-emerald-800 border-emerald-200',
              className,
            )}
          >
            {count} / {limitText} stores
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold">
              <span>Shopify stores in use (</span>{count} <span>of</span> {limitText}<span>)</span>
            </p>
            {domains.length === 0 ? (
              <p className="text-xs">No stores configured yet.</p>
            ) : (
              <ul className="text-xs list-disc list-inside space-y-0.5">
                {domains.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            )}
            <p className="text-xs italic">
              Counts unique domains across active integrations and custom pins.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
